import { Category, ScorecardData, Metric } from '../types/scorecard';

// Helper function to create metrics with team ownership, status and hierarchy
const createMetric = (
    id: string,
    name: string,
    description: string,
    owner: string = '',
    isActive: boolean = true,
    subMetrics: Metric[] = []
): Metric => ({
    id,
    name,
    description,
    value: '',
    target: '',
    owner,
    isActive,
    lastUpdated: new Date(),
    subMetrics
});

export const scorecardData4Categories: Category[] = [
    {
        id: 'client',
        name: 'CLIENT',
        description: 'Revenue & Market Access',
        goal: 'Pre-clearance (2026): generate CRO/NHS revenue to extend runway and build real-world evidence. Post-clearance (2027+): scaled US health system adoption and EU market entry following FDA 510(k) and CE marking.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Revenue',
                description: 'CLIENT — Pre-clearance pipeline → post-clearance scaled commercial deployment',
                metrics: [

                    // ═══════════════════════════════════════════════════════════
                    //  L1.1 — PRE-CLEARANCE REVENUE PIPELINE
                    //  Jan–Dec 2026 | Active
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-pre-clearance-revenue',
                        'L1.1  Pre-Clearance Revenue Pipeline',
                        'Generate CRO trial enrichment and NHS pilot revenue before FDA 510(k) / CE mark clearance. Primary runway extension and real-world evidence strategy for 2026.',
                        'CMO',
                        true,
                        [
                            // ─── L2.1 — CRO Acquisition & Pipeline ──────────────
                            createMetric(
                                'l2-cro-acquisition',
                                'CRO Acquisition & Pipeline',
                                'Build and convert pharma/CRO pipeline for pre-clearance ECG enrichment contracts. Track outreach, proposals and deal velocity.',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'daily-cro-outreach',
                                        'Weekly CRO Outreach',
                                        '# new CRO / pharma sponsor contacts engaged per week',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'proposal-pipeline',
                                        'Proposal Pipeline',
                                        '# active proposals pending sponsor decision',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'demo-requests',
                                        'Demo Requests',
                                        '# inbound product demo requests (inbound signal of brand traction)',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'decision-timeframe',
                                        'Decision Timeframe',
                                        'Average days from first contact to signed contract',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'revenue-pipeline',
                                        'Signed Deal Pipeline',
                                        'Total forward pipeline value from signed and near-signed CRO contracts (£)',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'cro-referral-network',
                                        'CRO Referral Network',
                                        '# warm referrals to new sponsors from existing CRO partners',
                                        'CMO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.2 — CRO Delivery & Revenue ──────────────────
                            createMetric(
                                'l2-cro-delivery-revenue',
                                'CRO Delivery & Revenue',
                                'Execute active CRO contracts: ECG throughput, turnaround time, enrichment performance, and pre-clearance per-ECG revenue. HoSWE owns technical delivery from June 2026.',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'trial-ecgs-processed',
                                        'Trial ECGs Processed',
                                        '# ECGs analysed across all active CRO studies (cumulative and per-week)',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'turnaround-time',
                                        'Turnaround Time',
                                        'Median hours from ECG receipt to analysis delivery to sponsor (SLA target: <24 h)',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'enrichment-performance',
                                        'Enrichment Performance',
                                        'AI-predicted event rate vs baseline in active trial; delta vs standard screening threshold',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'sponsor-satisfaction',
                                        'Sponsor Satisfaction',
                                        'Weekly partner satisfaction score (1–10 CSAT) across active CRO contracts',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'per-ecg-pricing',
                                        'Per-ECG Revenue Rate',
                                        'Current contracted revenue per ECG analysis (£); benchmark vs Anumana/Eko pricing',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'payment-collections',
                                        'Payment Collections',
                                        'Days sales outstanding (DSO) on issued CRO invoices; collections health',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'cro-contract-optimization',
                                        'Contract Optimisation',
                                        'Revenue per contract vs comparable SaMD benchmarks; renegotiation pipeline',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'multi-study-coordination',
                                        'Multi-Study Coordination',
                                        '# simultaneous CRO studies managed concurrently; scheduling conflict rate',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'automated-reporting-delivery',
                                        'Automated Report Delivery',
                                        '% CRO study interim reports auto-generated; report delivery latency vs SLA',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.3 — NHS Validation Pilot ────────────────────
                            createMetric(
                                'l2-nhs-pilot',
                                'NHS Validation Pilot',
                                'Run NHS pilot generating real-world ECG volume, clinical evidence for regulatory dossiers, and proof of clinical workflow integration. Active through 2026.',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'daily-ecg-volume',
                                        'Daily ECG Volume',
                                        '# ECGs processed through NHS pilot site per day (target: ramp to 50/day)',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'clinician-adoption',
                                        'Clinician Adoption',
                                        '# active clinicians integrating AI-ECG output into clinical decision-making',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'data-quality-score',
                                        'Data Quality Score',
                                        '% ECGs passing automated QC thresholds before analysis (rejects tracked)',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'technical-issues',
                                        'Technical Issues',
                                        '# integration / system issues raised per week; severity classification (P1–P3)',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'clinician-workflow-efficiency',
                                        'Clinician Workflow Efficiency',
                                        'Time saved per clinician per ECG review (minutes); measured vs pre-AI baseline',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'patient-pathway-optimization',
                                        'Patient Pathway Optimisation',
                                        '# referral pathway changes triggered by AI flag in NHS pilot site',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'interoperability-performance',
                                        'NHS Interoperability Performance',
                                        'System uptime and API latency vs NHS integration SLA',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'nhs-trust-expansion',
                                        'NHS Trust Expansion Pipeline',
                                        '# additional NHS trusts in onboarding discussion following pilot readout',
                                        'CMO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Events, KOL & Brand ─────────────────────
                            createMetric(
                                'l2-events-kol-brand',
                                'Events, KOL & Brand',
                                'Conference presence, KOL relationship building and brand development in interventional cardiology and clinical AI. Primary vehicle: ACC, ESC, HRS.',
                                'CEO',
                                true,
                                [
                                    createMetric(
                                        'acc-2026-contacts',
                                        'ACC 2026 — Contacts',
                                        '# meaningful contacts made at ACC 2026 (actual: 18/50 target)',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'acc-2026-meetings',
                                        'ACC 2026 — Follow-up Meetings',
                                        '# follow-up meetings booked from ACC 2026 contacts (actual: 6/18)',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'acc-2026-personal-followers',
                                        'ACC 2026 — CEO LinkedIn Growth',
                                        '# new followers on CEO personal LinkedIn post-ACC (actual: 60)',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'acc-2026-linkedin-followers',
                                        'ACC 2026 — Company LinkedIn Growth',
                                        '# new Cardiovolt.ai company page followers post-ACC (actual: 22)',
                                        'CEO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.5 — Clinical Evidence & Outcomes ────────────
                            createMetric(
                                'l2-clinical-evidence-outcomes',
                                'Clinical Evidence & Outcomes',
                                'Real-world clinical outcomes validating AI-ECG diagnostic value across NHS and CRO settings. Evidence base for 510(k)/CER regulatory dossiers and clinician confidence.',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'flag-accuracy',
                                        'AI Flag Accuracy',
                                        '% AI-flagged ECGs confirmed by clinician as true positive (per model)',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'clinical-actions',
                                        'Clinical Action Rate',
                                        '% AI flags resulting in a documented clinical intervention or onward referral',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'outcome-follow-up',
                                        'Outcome Follow-up Completion',
                                        '% AI-flagged patients with 30-day follow-up outcome recorded in NHS notes',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'adverse-events',
                                        'Adverse Events (PMS)',
                                        '# device-related adverse events or near-misses reported; vigilance pipeline (ISO 14971 / MDR)',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'population-health-impact',
                                        'Population Health Impact',
                                        'Estimated cardiovascular event reduction attributable to AI-ECG at pilot site level',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'health-economic-outcomes',
                                        'Health Economic Outcomes',
                                        'Cost per QALY delivered vs standard-of-care ECG pathway; NICE evidence modelling',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'long-term-outcome-tracking',
                                        'Long-term Outcome Studies',
                                        '# active 12-month and 5-year post-market outcome studies in progress',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'mortality-morbidity-impact',
                                        'Mortality & Morbidity Impact',
                                        'Population-level all-cause cardiovascular mortality reduction (PMCF evidence)',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                    // ═══════════════════════════════════════════════════════════
                    //  L1.2 — POST-CLEARANCE MARKET ENTRY
                    //  Jan 2027+ | Mostly inactive — activated on FDA/CE milestone
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-post-clearance-market-entry',
                        'L1.2  Post-Clearance Market Entry',
                        'Scaled commercial deployment across US health systems and EU markets following FDA 510(k) clearance (target: Dec 2026) and CE marking. Predominantly inactive until clearance milestones are hit.',
                        'CMO',
                        false,
                        [
                            // ─── L2.1 — US Health System Adoption ───────────────
                            createMetric(
                                'l2-us-health-system-adoption',
                                'US Health System Adoption',
                                'EHR-integrated deployment into US health systems post-510(k). Epic App Orchard pipeline, FHIR certification, and scalable deployment automation.',
                                'CMO',
                                false,
                                [
                                    createMetric(
                                        'pilot-health-systems',
                                        'Paid Pilot Health Systems',
                                        '# US health systems in paid commercial pilot post-clearance',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'epic-integration-progress',
                                        'Epic Integration Progress',
                                        '% Epic App Orchard certification and integration complete',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'api-certification',
                                        'EHR API Certification',
                                        '# EHR vendor certifications achieved (Epic, Oracle Health, etc.)',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'multi-ehr-compatibility-testing',
                                        'Multi-EHR Compatibility',
                                        '# EHR platforms with validated FHIR R4 integration and regression test pass rate',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'enterprise-deployment-automation',
                                        'Enterprise Deployment Automation',
                                        '# health systems onboarded via automated provisioning workflow (target: <5 days)',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'white-label-partnership-development',
                                        'White-label Partnerships',
                                        '# EHR vendor white-label or embedded integration agreements signed',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'interoperability-standard-compliance',
                                        'Interoperability Standards Compliance',
                                        'FHIR R4 / HL7 / ONC certification status; USCDI conformance',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.2 — Revenue Scaling & Unit Economics ─────────
                            createMetric(
                                'l2-revenue-scaling',
                                'Revenue Scaling & Unit Economics',
                                'Post-clearance MRR growth through dynamic pricing, contract renewals, upsell across therapeutic areas, and global CRO expansion.',
                                'CMO',
                                false,
                                [
                                    createMetric(
                                        'monthly-recurring-revenue',
                                        'Monthly Recurring Revenue',
                                        'MRR from live health system and CRO contracts combined (£); month-on-month growth %',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'dynamic-pricing-optimization',
                                        'Dynamic Pricing Optimisation',
                                        'AI-driven per-analysis pricing vs static contract benchmarks; volume-tier modelling',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'customer-lifetime-value-tracking',
                                        'Customer Lifetime Value',
                                        'CLV per health system and CRO partner segment; cohort LTV trends',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'revenue-per-therapeutic-area',
                                        'Revenue by Therapeutic Area',
                                        'Segmented revenue: AFib, LVH, cardiomyopathy, HCM, LQTS, etc.',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'contract-renewal-revenue-growth',
                                        'Contract Renewal Growth',
                                        '% revenue increase on renewed CRO / health system contracts at renewal',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'upsell-cross-sell-conversion',
                                        'Upsell Conversion',
                                        '% existing customers purchasing additional AI model bundles (e.g. adding LVH to AFib)',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'global-cro-expansion',
                                        'Global CRO Expansion',
                                        '# international CRO contracts signed outside UK/US (EU, APAC)',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'competitive-cro-analysis',
                                        'CRO Win/Loss Analysis',
                                        'Win rate and documented loss reasons vs Anumana, Eko Health, AliveCor',
                                        'CMO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.3 — Clinician Adoption & Long-term Impact ────
                            createMetric(
                                'l2-clinician-advocacy',
                                'Clinician Adoption & Long-term Impact',
                                'Post-clearance workflow integration depth, clinician satisfaction, and long-term health economic evidence for reimbursement and PMCF requirements.',
                                'CMO',
                                false,
                                [
                                    createMetric(
                                        'clinical-engagement-score',
                                        'Clinical Engagement Score',
                                        'Composite clinician satisfaction / NPS score across all deployed health system sites',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'clinician-adoption-rate-post-clearance',
                                        'Clinician Adoption Rate',
                                        '% of eligible clinicians at deployed sites actively using AI-ECG in workflow (monthly)',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'workflow-efficiency-post-clearance',
                                        'Workflow Efficiency at Scale',
                                        'Time saved per clinician per ECG review across all live sites (vs pre-AI baseline)',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'patient-pathway-outcomes-post-clearance',
                                        'Patient Pathway Outcomes',
                                        '# guideline-concordant referral pathway changes attributable to AI-ECG at scale',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'health-economics-reimbursement',
                                        'Health Economics & Reimbursement',
                                        'Cost per QALY at scale; NICE / CMS reimbursement submission readiness',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'pmcf-long-term-study',
                                        'PMCF Long-term Study',
                                        '# patients enrolled in PMCF / post-market outcome study per MDR / FDA PDMA',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — NHS Trust & EU Market Expansion ──────────
                            createMetric(
                                'l2-nhs-eu-expansion',
                                'NHS Trust & EU Market Expansion',
                                'Expand across NHS trusts post-CE marking and enter European health system markets; leverage EUDAMED registration and NHS national programme channels.',
                                'CMO',
                                false,
                                [
                                    createMetric(
                                        'nhs-trust-commercial',
                                        'NHS Trust Commercial Agreements',
                                        '# NHS trusts signed to post-pilot commercial contracts (beyond validation site)',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'nhs-national-programme',
                                        'NHS National Programme Access',
                                        'Progress on NHSE AI in Health and Care / NICE early access programme engagement',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'eu-health-system-pipeline',
                                        'EU Health System Pipeline',
                                        '# EU health systems in active commercial discussion post-CE marking',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'eu-distributor-agreements',
                                        'EU Distributor Agreements',
                                        '# local distribution / value-added reseller agreements signed in EU markets',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'reimbursement-pathway-progress',
                                        'Reimbursement Pathway Progress',
                                        'NHS / NICE and EU national reimbursement application status and timeline',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.5 — Competitive Positioning & Market Access ──
                            createMetric(
                                'l2-competitive-market-access',
                                'Competitive Positioning & Market Access',
                                'Maintain and communicate clinical and commercial advantage vs Anumana, Eko Health, AliveCor and diagnostic AI peers in US and EU health system sales cycles.',
                                'CMO',
                                false,
                                [
                                    createMetric(
                                        'competitive-win-rate-analysis',
                                        'Competitive Win Rate',
                                        '% head-to-head health system / CRO pilots won vs named competitors',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'market-share-tracking',
                                        'AI-ECG Market Share',
                                        'Estimated % of AI-ECG SaMD market by volume (health systems + CRO combined)',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'thought-leadership-commercial-impact',
                                        'Thought Leadership Commercial Impact',
                                        '# peer-reviewed publications cited by prospects in commercial due diligence',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'technology-differentiation-messaging',
                                        'Technology Differentiation',
                                        'Win interview score on "clinical evidence superiority" vs competitor benchmarks',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'social-media-competitive-sentiment',
                                        'Competitive Brand Sentiment',
                                        'Share-of-voice vs competitors on LinkedIn / medical social; brand index score',
                                        'CMO',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                ]
            }
        ]
    },
    {
        id: 'control',
        name: 'CONTROL',
        description: 'Regulatory, Compliance & Certification',
        goal: 'Dual regulatory pathway: achieve FDA 510(k) clearance and EU MDR CE marking by Dec 2026. Underpin both submissions with a certified QMS (ISO 13485), complete risk management (ISO 14971), and IEC 62304-compliant software lifecycle. Post-clearance: maintain PMS, PMCF, vigilance, and pursue De Novo / multi-model expansion.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Compliance',
                description: 'CONTROL — Regulatory clearance 2026 → post-market compliance & future pathways',
                metrics: [

                    // ═══════════════════════════════════════════════════════════
                    //  L1.1 — REGULATORY CLEARANCE 2026
                    //  Active | Target: 510(k) + CE mark submissions Dec 2026
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-regulatory-clearance-2026',
                        'L1.1  Regulatory Clearance 2026',
                        'Achieve FDA 510(k) clearance and EU MDR CE marking by end of 2026. Underpinned by ISO 13485-certified QMS, ISO 14971 risk files, and IEC 62304-compliant software lifecycle. Dual submission strategy mirrors Anumana and Eko Health regulatory playbooks.',
                        'CSO',
                        true,
                        [
                            // ─── L2.1 — FDA 510(k) Submission ───────────────────
                            createMetric(
                                'l2-fda-510k',
                                'FDA 510(k) Submission',
                                'Lead AI ECG 510(k) (LEF) for ECG-based SaMD. Predicate: Anumana ECG-AI. Target: eSTAR submission Dec 2026; FDA pre-sub meeting Q2 2026. JREG as regulatory consultant.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'predicate-analysis',
                                        'Predicate Device Analysis',
                                        'Anumana ECG-AI predicate comparison: substantial equivalence argument drafted and reviewed',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'clinical-data-package',
                                        'Clinical Data Package',
                                        '% clinical evidence complete for 510(k) dossier (9 models × sensitivity/specificity datasets)',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'fda-pre-sub-meeting',
                                        'FDA Pre-Submission Meeting',
                                        'Q-sub submitted; meeting confirmed; key questions on AI/ML and predicate resolved',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'estar-submission-prep',
                                        'eSTAR Package Completion',
                                        '% of eSTAR template sections completed and reviewed (target: 100% by Nov 2026)',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'regulatory-consultant-progress',
                                        'JREG Consultant Milestones',
                                        'JREG deliverable tracker: SOW tasks completed vs scheduled; open actions',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'fda-annual-report-compliance',
                                        'FDA Annual Report (MDR)',
                                        'Post-clearance MDR annual report submission compliance (510(k) holder obligations)',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'fda-inspection-readiness',
                                        'FDA QS Inspection Readiness',
                                        'Readiness for FDA Quality System inspection; mock audit score vs benchmark',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'breakthrough-device-designation-maintenance',
                                        'Breakthrough Device Designation',
                                        'Status of Breakthrough Device Designation; interaction programme benefits utilised',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.2 — EU MDR CE Marking ───────────────────────
                            createMetric(
                                'l2-eumdr-ce-marking',
                                'EU MDR CE Marking',
                                'Class IIa SaMD under EU MDR 2017/745. NB: Scarlet (contract stage). Technical file per Annex II/III. CER covering 9 AI-ECG models. Target: submission Dec 2026, CE mark Q2 2027.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'notified-body-engagement',
                                        'Notified Body Engagement',
                                        'Scarlet contract status; Stage 1/2 audit dates confirmed; open NB queries resolved',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'technical-file-completion',
                                        'Technical File Completion',
                                        '% Technical File sections completed per Annex II (target: 100% by Nov 2026)',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'clinical-evaluation-report',
                                        'Clinical Evaluation Report',
                                        'CER status: all 9 AI-ECG models covered per MDR Annex XIV; literature search complete',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'usability-engineering-file',
                                        'Usability Engineering File',
                                        'IEC 62366-1 usability file: formative and summative testing complete; HoSWE leads from June 2026',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'pmcf-plan-development',
                                        'PMCF Plan',
                                        'Post-Market Clinical Follow-up plan drafted per MDR Annex XIV Part B; PMCF study protocol approved',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'eudamed-compliance-tracking',
                                        'EUDAMED Registration',
                                        'SRN registration, UDI assignment and EUDAMED device record complete (post-CE marking)',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'mdr-periodic-safety-update-reports',
                                        'PSUR Compliance',
                                        'Periodic Safety Update Report generated and submitted per MDR Article 86 cadence (post-CE)',
                                        'QARA',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.3 — QMS & ISO 13485 ─────────────────────────
                            createMetric(
                                'l2-qms-iso13485',
                                'QMS & ISO 13485 Certification',
                                'Build and certify the CardioVolt QMS to ISO 13485:2016 by Jun 2026 (Stage 1 audit) and full certification. Foundation for both 510(k) and EU MDR submissions. QARA leads.',
                                'QARA',
                                true,
                                [
                                    createMetric(
                                        'sop-development',
                                        'SOP Development',
                                        '# SOPs written vs total required for ISO 13485 scope; approval status',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'document-control',
                                        'Document Control System',
                                        'QMS document control system live; version control, access rights and review cycles operational',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'management-review-cadence',
                                        'Management Review',
                                        'Management review meetings held per QMS schedule; minutes and action log maintained',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'internal-audit-schedule',
                                        'Internal Audit Schedule',
                                        '# internal audits planned vs completed; findings and NCR closure rate',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'capa-system',
                                        'CAPA System',
                                        'CAPA system live: # open CAPAs, average days to close, repeat finding rate',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'qms-maturity-assessment',
                                        'QMS Maturity Assessment',
                                        'Periodic benchmarking of QMS maturity vs ISO 13485 best practice (annual)',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'quality-cost-optimization',
                                        'Quality Cost Optimisation',
                                        'Cost of quality tracking: appraisal, failure and prevention costs as % of revenue',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'multi-site-qms-coordination',
                                        'Multi-site QMS Coordination',
                                        'QMS scope extension to US entity and any future manufacturing/distribution sites',
                                        'QARA',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Risk Management ISO 14971 ───────────────
                            createMetric(
                                'l2-risk-management-14971',
                                'Risk Management ISO 14971',
                                'Maintain complete ISO 14971:2019 risk management files for all 9 AI-ECG models. Feeds directly into 510(k) Safety and Performance Summary and MDR Technical File Annex I. QARA leads.',
                                'QARA',
                                true,
                                [
                                    createMetric(
                                        'hazard-analysis',
                                        'Hazard Identification',
                                        '# hazards identified per AI-ECG model; coverage vs IMDRF AI/ML safety taxonomy',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'risk-control-measures',
                                        'Risk Control Measures',
                                        '# risk controls implemented and verified; residual risk acceptability documented',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'residual-risk-evaluation',
                                        'Residual Risk Evaluation',
                                        'Post-mitigation residual risk acceptability confirmed per risk management plan',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'risk-benefit-analysis',
                                        'Risk–Benefit Analysis',
                                        'Overall benefit-risk determination documented and signed off per ISO 14971 §9',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'fmea-completion',
                                        'FMEA Completion',
                                        '% FMEA analysis coverage across all software components and use scenarios',
                                        'QARA',
                                        true
                                    ),
                                    createMetric(
                                        'dynamic-risk-assessment',
                                        'Post-Market Risk Monitoring',
                                        'Real-time risk monitoring from PMS/PMCF data feeding back into risk management file',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'enterprise-risk-integration',
                                        'Enterprise Risk Integration',
                                        'ISO 14971 risk files integrated with corporate risk register and cybersecurity risk assessment',
                                        'QARA',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.5 — IEC 62304 Software Lifecycle ────────────
                            createMetric(
                                'l2-iec62304-lifecycle',
                                'IEC 62304 Software Lifecycle',
                                'Implement IEC 62304:2006+A1 software development lifecycle for all AI-ECG software units. HoSWE (Francesco) leads full 62304 programme from June 2026, completing handover from CTO. Required for both 510(k) and MDR Technical File.',
                                'HoSWE',
                                true,
                                [
                                    createMetric(
                                        'software-safety-classification',
                                        'Software Safety Classification',
                                        'Class A/B/C classification documented for all software items and AI/ML model components',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'software-development-plan',
                                        'Software Development Plan',
                                        'IEC 62304-compliant Software Development Plan approved and version-controlled',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'software-requirements-spec',
                                        'Software Requirements Specification',
                                        'SRS complete: all functional and performance requirements traceable to design outputs',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'software-design-spec',
                                        'Software Design Specification',
                                        'SDS complete: architecture, SOUP inventory, interfaces documented per IEC 62304 §5.4',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'verification-validation-plan',
                                        'V&V Plan & Execution',
                                        'Software V&V plan approved; all test protocols executed and results documented (target: Jul–Nov 2026)',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'ai-model-version-control-compliance',
                                        'AI Model Change Control',
                                        'Model version control integrated with 62304 change management: every model update triggers impact assessment',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                    // ═══════════════════════════════════════════════════════════
                    //  L1.2 — POST-MARKET COMPLIANCE & FUTURE PATHWAYS
                    //  Mostly inactive — activates on clearance milestones
                    //  Exception: Cybersecurity (L2.1) is active pre-clearance
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-post-market-compliance',
                        'L1.2  Post-Market Compliance & Future Pathways',
                        'Ongoing regulatory compliance obligations post-clearance (PMS, PMCF, vigilance, cybersecurity) plus future expansion pathways (De Novo, multi-model, AI/ML regulatory framework). Cybersecurity is active now as required for 510(k) cyber submissions and MDR Annex I §17.',
                        'QARA',
                        false,
                        [
                            // ─── L2.1 — Cybersecurity IEC 81001-5-1 ─────────────
                            // NOTE: Active now — cybersecurity documentation required
                            // for both 510(k) (FDA Cyber guidance 2023) and MDR Annex I §17.
                            // HoSWE owns from June 2026.
                            createMetric(
                                'l2-cybersecurity',
                                'Cybersecurity — IEC 81001-5-1',
                                'Implement IEC 81001-5-1 cybersecurity lifecycle for AI-ECG SaMD. Required for FDA 510(k) (2023 Cyber guidance) and MDR Annex I §17. HoSWE leads from June 2026. Active pre-clearance; continuous post-clearance.',
                                'HoSWE',
                                true,
                                [
                                    createMetric(
                                        'threat-modeling',
                                        'Threat Modelling',
                                        'Threat model complete (STRIDE/MITRE ATT&CK for Healthcare); documented per IEC 81001-5-1',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'penetration-testing',
                                        'Penetration Testing',
                                        'External pen test completed; findings tracked by severity; re-test scheduled',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'vulnerability-management',
                                        'Vulnerability Management',
                                        '# open vulnerabilities by severity (Critical/High/Med/Low); mean time to remediate',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'sbom-maintenance',
                                        'SBOM Maintenance',
                                        'Software Bill of Materials current and version-controlled; SOUP CVE monitoring active',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'continuous-security-monitoring',
                                        'Continuous Security Monitoring',
                                        '24/7 automated security monitoring pipeline live; alert triage SLA defined',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'compliance-framework-integration',
                                        'Compliance Framework Integration',
                                        'SOC 2 Type II / HIPAA / NHS DSP Toolkit alignment mapped to IEC 81001-5-1 controls',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.2 — Post-Market Surveillance & PMCF ─────────
                            createMetric(
                                'l2-pms-pmcf',
                                'Post-Market Surveillance & PMCF',
                                'EU MDR PMS obligations (Art. 83–86) and PMCF execution (Annex XIV Part B) post-CE marking. FDA MDR reporting and annual reports post-510(k) clearance. QARA leads.',
                                'QARA',
                                false,
                                [
                                    createMetric(
                                        'pmcf-study-execution',
                                        'PMCF Study Execution',
                                        'PMCF study recruitment progress vs protocol; enrolment rate and data completeness',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'pms-data-collection',
                                        'PMS Data Collection',
                                        'Post-market data sources active: complaint log, literature, registry, NB feedback — completeness %',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'eudamed-compliance-tracking',
                                        'EUDAMED Compliance',
                                        'EUDAMED device record, UDI and post-market report submissions current and compliant',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'mdr-periodic-safety-update-reports',
                                        'PSUR Generation',
                                        'Periodic Safety Update Report generated per MDR Art. 86 cadence; NB submission confirmed',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'fda-annual-report-compliance',
                                        'FDA Annual MDR Report',
                                        'Annual report submitted to FDA per 510(k) holder obligations; on-time filing rate',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'fda-inspection-readiness',
                                        'FDA QS Inspection Readiness',
                                        'Mock audit completed; QMS inspection-ready; all records available and retrievable',
                                        'QARA',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.3 — Vigilance & Complaint Handling ───────────
                            createMetric(
                                'l2-vigilance-complaints',
                                'Vigilance & Complaint Handling',
                                'EU MDR vigilance reporting (Art. 87–90) and FDA MDR complaint handling post-clearance. Complaint management SOP live. Adverse events escalated per QARA SOP.',
                                'QARA',
                                false,
                                [
                                    createMetric(
                                        'complaint-intake-processing',
                                        'Complaint Intake & Processing',
                                        '# complaints received; % processed within SOP timeframe; classification by severity',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'serious-incident-reporting',
                                        'Serious Incident Reporting',
                                        '# serious incidents reported to NB / FDA per MDR Art. 87 / 21 CFR Part 803; on-time rate',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'field-safety-corrective-actions',
                                        'Field Safety Corrective Actions',
                                        '# FSCAs initiated; FSCA effectiveness confirmed; recall/advisory notice issued if required',
                                        'QARA',
                                        false
                                    ),
                                    createMetric(
                                        'trend-analysis-reporting',
                                        'Trend Analysis & Reporting',
                                        'Statistical trend analysis of PMS / complaint data; non-serious trending incidents escalated',
                                        'QARA',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — De Novo & Multi-model Pathway ───────────
                            createMetric(
                                'l2-de-novo-multimodel',
                                'De Novo & Multi-model Expansion',
                                'FDA De Novo pathway for novel AI-ECG indications without a predicate device. Multi-model bundle strategy post-clearance to expand beyond initial 510(k) scope. All inactive until initial clearance.',
                                'CSO',
                                false,
                                [
                                    createMetric(
                                        'novel-predicate-research',
                                        'Novel Predicate Research',
                                        'Landscape review for De Novo indications: models without Anumana or similar predicate',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'multi-model-validation',
                                        'Multi-model Bundle Validation',
                                        '# additional AI-ECG models validated beyond initial 9-model submission scope',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'clinical-evidence-expansion',
                                        'Clinical Evidence Expansion',
                                        'Additional clinical studies initiated to support De Novo or expanded 510(k) indications',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'fda-breakthrough-designation',
                                        'Breakthrough Device Designation',
                                        'Breakthrough Device Designation application or renewal progress for expanded model bundle',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'breakthrough-device-designation-maintenance',
                                        'Breakthrough Designation Benefits',
                                        'Active utilisation of Breakthrough programme interaction benefits: priority review, meetings',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.5 — AI/ML Regulatory Framework ──────────────
                            createMetric(
                                'l2-aiml-regulatory-framework',
                                'AI/ML Regulatory Science & Framework',
                                'Shape and track the evolving AI/ML SaMD regulatory landscape. Position CardioVolt as a thought leader in FDA PCCP (Predetermined Change Control Plan) and EU AI Act compliance for medical AI.',
                                'CSO',
                                false,
                                [
                                    createMetric(
                                        'ai-ml-regulatory-framework-development',
                                        'AI/ML Regulatory Framework',
                                        'PCCP (Predetermined Change Control Plan) drafted per FDA AI/ML Action Plan; EU AI Act risk tier confirmed',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'pccp-submission',
                                        'PCCP Submission',
                                        'Predetermined Change Control Plan submitted as part of or supplement to 510(k) for continuous learning models',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'eu-ai-act-compliance',
                                        'EU AI Act Compliance',
                                        'EU AI Act high-risk AI system obligations mapped; conformity assessment approach agreed with NB',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'regulatory-science-publications',
                                        'Regulatory Science Publications',
                                        '# publications or white papers contributing to AI/ML SaMD regulatory science (IMDRF, FDA dockets)',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                ]
            }
        ]
    },
    {
        id: 'cost',
        name: 'COST',
        description: 'Burn, Runway & Unit Economics',
        goal: 'Pre-clearance (2026): control burn, extend runway via CRO/NHS revenue, and close Series A/bridge. Post-clearance (2027+): achieve >75% gross margin on software analyses as volume scales.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Financials',
                description: 'COST — Runway control & fundraising → unit economics & scale profitability',
                metrics: [

                    // ═══════════════════════════════════════════════════════════
                    //  L1.1 — RUNWAY & FINANCIAL CONTROL
                    //  Active | 2026 focus: burn management, revenue vs runway,
                    //  fundraising execution
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-runway-financial-control',
                        'L1.1  Runway & Financial Control',
                        'Manage cash burn and extend runway to regulatory clearance (target: Dec 2026). Combine cost discipline, pre-clearance CRO/NHS revenue, and Series A fundraising to maintain ≥12 months runway at all times.',
                        'CSO',
                        true,
                        [
                            // ─── L2.1 — Burn Rate & Opex Control ────────────────
                            createMetric(
                                'l2-burn-rate-opex',
                                'Burn Rate & Opex Control',
                                'Monitor total cash consumption by category. Identify and address cost overruns before they compress runway. CSO owns; HoSWE accountable for infrastructure line.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'daily-cash-burn',
                                        'Monthly Cash Burn',
                                        'Total cash outflow per month (£); actual vs budget; month-on-month trend',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'payroll-expenses',
                                        'Payroll & Contractor Costs',
                                        'Total personnel costs (payroll + contractor fees) as % of total burn; headcount cost per FTE',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'regulatory-consulting-costs',
                                        'Regulatory & NB Consulting Costs',
                                        'External spend on JREG, Scarlet NB, QARA consultancy; tracked vs regulatory budget',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'departmental-cost-center-optimization',
                                        'Cost Centre Optimisation',
                                        'Budget vs actual variance by department; identification of efficiency savings by business unit',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'tax-optimization-strategy-execution',
                                        'Tax Optimisation',
                                        'R&D tax credit claims filed; EIS/SEIS investor relief compliance; tax efficiency vs benchmark',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.2 — R&D & Infrastructure Spend ──────────────
                            createMetric(
                                'l2-rd-infra-spend',
                                'R&D & Infrastructure Spend',
                                'Track R&D expenditure (model development, V&V, clinical studies) and cloud/compute infrastructure costs. HoSWE owns infrastructure cost line from June 2026.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'rd-expenses',
                                        'R&D Expenditure',
                                        'Total R&D spend (model dev, V&V, clinical studies) as % of total burn; vs HMRC R&D credit eligibility',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'cloud-infrastructure-costs',
                                        'Cloud & Compute Costs',
                                        'AWS/GCP monthly spend: GPU training, inference, storage, data transfer; tracked vs model volume',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'rd-to-opex-ratio',
                                        'R&D : Opex Ratio',
                                        'R&D spend as % of total opex; benchmark: SaMD pre-clearance peers typically 55–70%',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'months-runway',
                                        'Months Runway',
                                        'Months of runway at current burn rate (cash balance ÷ monthly net burn); target: ≥12 months at all times',
                                        'CSO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.3 — Pre-clearance Revenue vs Runway ─────────
                            createMetric(
                                'l2-revenue-vs-runway',
                                'Pre-clearance Revenue vs Runway',
                                'Track how CRO and NHS revenue is offsetting burn and extending runway to clearance. Critical bridge metric for 2026.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'revenue-to-burn-ratio',
                                        'Revenue : Burn Ratio',
                                        'Monthly CRO + NHS revenue as % of total monthly burn (target: >30% by Q4 2026)',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'runway-extension-from-revenue',
                                        'Runway Extension from Revenue',
                                        '# additional months of runway attributable to pre-clearance revenue vs burn-only scenario',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'cro-revenue-monthly',
                                        'Monthly CRO Revenue',
                                        'Total invoiced CRO revenue per month (£); actual vs pipeline forecast',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'revenue-vs-budget',
                                        'Revenue vs Budget',
                                        'Actual total revenue vs annual revenue budget; variance tracked monthly with reforecast',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'cost-of-revenue',
                                        'Cost of Revenue',
                                        'Direct costs attributable to delivering CRO and NHS analyses (compute, labour, support) as % of revenue',
                                        'CSO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Fundraising Execution ───────────────────
                            createMetric(
                                'l2-fundraising-execution',
                                'Fundraising Execution',
                                'Active Series A / bridge round management. Track investor pipeline velocity from first meeting to term sheet. CSO leads.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'investor-meetings-scheduled',
                                        'Investor Meetings',
                                        '# investor meetings held per week; pipeline stage breakdown (intro / deep-dive / DD)',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'term-sheet-negotiations',
                                        'Term Sheet Negotiations',
                                        '# active term sheet discussions; key terms under negotiation; expected close date',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'investor-follow-up-queue',
                                        'Investor Follow-up Queue',
                                        '# investors with open action items (materials requested, questions pending); clearance rate',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'pitch-conversion-rate',
                                        'Pitch Conversion Rate',
                                        '% of first meetings progressing to due diligence stage; funnel conversion by investor tier',
                                        'CSO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.5 — Round Readiness & Investor Relations ─────
                            createMetric(
                                'l2-round-readiness',
                                'Round Readiness & Investor Relations',
                                'Data room completeness, investor relationship depth, and secondary market awareness for Series A close.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'due-diligence-readiness',
                                        'Data Room Completeness',
                                        '% of investor data room sections complete and current; outstanding items tracked',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'institutional-investor-relationship-depth',
                                        'Institutional Investor Relationship Depth',
                                        'Tier 1 VC relationship scoring; warm vs cold pipeline ratio; re-engagement cadence',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'roadshow-effectiveness-metrics',
                                        'Roadshow Effectiveness',
                                        'Meeting-to-term-sheet conversion rate per roadshow cohort; feedback themes tracked',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'secondary-market-activity-monitoring',
                                        'Secondary Market Monitoring',
                                        'Secondary share activity; existing shareholder liquidity requests; cap table dynamics',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                    // ═══════════════════════════════════════════════════════════
                    //  L1.2 — UNIT ECONOMICS & SCALE PROFITABILITY
                    //  Mostly inactive — models built pre-clearance, metrics
                    //  activated once commercial volume begins (Jan 2027+)
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-unit-economics',
                        'L1.2  Unit Economics & Scale Profitability',
                        'Model and then track post-clearance unit economics: cost per ECG analysis at scale, gross margin trajectory to >75%, and customer segment profitability. Models can be built pre-clearance; metrics activate on commercial launch.',
                        'CSO',
                        false,
                        [
                            // ─── L2.1 — Cost Per Analysis Modelling ─────────────
                            createMetric(
                                'l2-cost-per-analysis',
                                'Cost Per Analysis Modelling',
                                'Model and track total cost to deliver one AI-ECG analysis at 1k / 10k / 100k ECG volume tiers. Target: <£1 per analysis at scale to achieve >75% gross margin.',
                                'CSO',
                                false,
                                [
                                    createMetric(
                                        'cost-per-analysis',
                                        'Cost Per Analysis',
                                        'Fully-loaded cost per ECG analysis at current volume; modelled cost at 10k and 100k/month scale',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'support-cost-allocation',
                                        'Support Cost Per Analysis',
                                        'Customer support and onboarding cost allocated per ECG analysis at current volume',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'regulatory-overhead-amortization',
                                        'Regulatory Overhead Amortisation',
                                        'Total regulatory spend (QMS, NB, consultants) amortised per analysis unit at target volume',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'cost-curve-projection',
                                        'Cost Curve Projection',
                                        'Projected cost per analysis at 12 / 24 / 36 months post-launch vs SaMD peer benchmarks',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.2 — Infrastructure & ML Efficiency ──────────
                            createMetric(
                                'l2-infra-ml-efficiency',
                                'Infrastructure & ML Efficiency',
                                'Optimise compute, storage and ML inference costs as volume scales. HoSWE owns. Target: GPU inference cost reduction >50% vs initial deployment through batching and model quantisation.',
                                'HoSWE',
                                false,
                                [
                                    createMetric(
                                        'compute-cost-optimization',
                                        'Compute Cost Optimisation',
                                        'GPU inference cost per ECG analysis; batching efficiency; spot instance utilisation rate',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'storage-cost-modeling',
                                        'Storage Cost Modelling',
                                        'Data retention cost per ECG at 1M / 10M ECG archive; cold vs hot storage tiering',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'ml-model-efficiency-optimization',
                                        'ML Inference Efficiency',
                                        'Model inference latency and memory footprint; quantisation / distillation savings vs baseline',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'third-party-api-cost-management',
                                        'Third-party API Cost Management',
                                        'External API and service costs per analysis; build vs buy decisions tracked by service',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'cogs-reduction-initiatives',
                                        'COGS Reduction Initiatives',
                                        'Active initiatives to reduce direct cost of goods sold; savings achieved vs baseline (£/month)',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.3 — Gross Margin Optimisation ───────────────
                            createMetric(
                                'l2-gross-margin',
                                'Gross Margin Optimisation',
                                'Track path to >75% gross software margin. Benchmark: Eko Health ~72%, Anumana undisclosed, SaMD software peers median ~68–78%. Achieve through volume, compute efficiency and pricing.',
                                'CSO',
                                false,
                                [
                                    createMetric(
                                        'gross-margin-target',
                                        'Gross Margin %',
                                        'Actual gross margin % (revenue minus direct COGS); target: >75% at 10k ECGs/month',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'margin-improvement-roadmap',
                                        'Margin Improvement Roadmap',
                                        'Quarterly margin improvement milestones; actions mapped to compute, pricing and volume levers',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'saas-vs-service-margin',
                                        'SaaS vs Service Margin Split',
                                        'Pure software margin (API delivery) vs managed service margin (CRO enrichment); trend over time',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'economies-of-scale-realization',
                                        'Economies of Scale Realisation',
                                        'Actual cost per analysis reduction vs volume increase; scale factor vs model prediction',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Customer Segment Profitability ───────────
                            createMetric(
                                'l2-segment-profitability',
                                'Customer Segment Profitability',
                                'Margin analysis by customer type (health system vs CRO) and therapeutic area. Informs go-to-market prioritisation and pricing strategy post-clearance.',
                                'CMO',
                                false,
                                [
                                    createMetric(
                                        'customer-segment-profitability-analysis',
                                        'Segment Profitability Analysis',
                                        'Gross margin by segment: health system vs CRO vs pharma; direct cost allocation by segment',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'cro-vs-health-system-margin',
                                        'CRO vs Health System Margin',
                                        'Margin comparison: pre-clearance CRO model vs post-clearance health system SaaS model',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'therapeutic-area-profitability',
                                        'Therapeutic Area Profitability',
                                        'Margin by indication: AFib vs LVH vs cardiomyopathy vs multi-model bundle',
                                        'CMO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.5 — CAC, LTV & Scale Economics ──────────────
                            createMetric(
                                'l2-cac-ltv-scale',
                                'CAC, LTV & Scale Economics',
                                'Customer acquisition cost and lifetime value metrics. LTV:CAC ratio is the primary commercial efficiency signal for health system sales. Target: LTV:CAC > 5× at steady state.',
                                'CSO',
                                false,
                                [
                                    createMetric(
                                        'customer-acquisition-cost',
                                        'Customer Acquisition Cost (CAC)',
                                        'Total sales & marketing cost ÷ # new customers acquired; by segment (health system vs CRO)',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'ltv-cac-ratio',
                                        'LTV : CAC Ratio',
                                        'Customer lifetime value ÷ acquisition cost; target >5× for health system segment',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'payback-period',
                                        'CAC Payback Period',
                                        'Months to recover customer acquisition cost from gross profit; target: <18 months',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'net-revenue-retention',
                                        'Net Revenue Retention',
                                        'NRR % from existing customers (renewals + upsell − churn); target: >110% at scale',
                                        'CMO',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                ]
            }
        ]
    },
    {
        id: 'colleague',
        name: 'COLLEAGUE',
        description: 'Team, IP & Network',
        goal: 'Land HoSWE and complete 62304 handover by June 2026. Protect IP with Imperial College. Build the scientific publication moat. Develop KOL and clinical network to underpin commercial and regulatory credibility.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Talent',
                description: 'COLLEAGUE — Critical talent & IP moat → strategic network, succession & product readiness',
                metrics: [

                    // ═══════════════════════════════════════════════════════════
                    //  L1.1 — CRITICAL TALENT, IP & SCIENTIFIC MOAT
                    //  Active | Key hires, IP protection, publications, retention
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-talent-ip-moat',
                        'L1.1  Critical Talent, IP & Scientific Moat',
                        'Secure and retain the critical technical and regulatory expertise needed for regulatory clearance. Protect the AI-ECG IP portfolio and build the peer-reviewed evidence moat that differentiates Cardiovolt from Anumana, Eko Health and peers.',
                        'CEO',
                        true,
                        [
                            // ─── L2.1 — HoSWE Hire & 62304 Programme Launch ─────
                            createMetric(
                                'l2-hoswe-hire-handover',
                                'HoSWE Hire & 62304 Programme Launch',
                                'Recruit, onboard and integrate Francesco Iori (HoSWE / IEC 62304 Lead). Complete structured handover of all IEC 62304 deliverables from CTO to HoSWE by June 2026. This is the single most critical hiring milestone for the 510(k) and MDR submissions.',
                                'CEO',
                                true,
                                [
                                    createMetric(
                                        'iec-62304-developer-hiring',
                                        'HoSWE Role — Hire Status',
                                        'Recruitment pipeline status: offer accepted, onboarding timeline, start date confirmed (target: June 2026)',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'candidate-sourcing',
                                        'Candidate Sourcing Pipeline',
                                        '# qualified IEC 62304 / SaMD software engineering candidates in active pipeline',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'technical-assessment-results',
                                        'Technical Assessment Results',
                                        '% of shortlisted candidates passing IEC 62304 / Python infrastructure technical assessment',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'hiring-time-to-offer',
                                        'Time to Offer',
                                        'Days from role posting to signed offer letter; benchmark vs target (<45 days for senior technical hire)',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'iec-62304-handover-progress',
                                        'IEC 62304 Handover Progress',
                                        '% of IEC 62304 programme deliverables formally handed from CTO to HoSWE; open items tracked',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'cto-hoswe-knowledge-transfer',
                                        'CTO → HoSWE Knowledge Transfer',
                                        'Structured knowledge transfer completion: codebase, architecture, V&V plans, cybersecurity posture',
                                        'CEO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.2 — Regulatory & QA Expertise ───────────────
                            createMetric(
                                'l2-regulatory-qa-expertise',
                                'Regulatory & QA Expertise',
                                'Build and maintain the in-house and consultancy regulatory expertise needed to own both the 510(k) and MDR submissions. Track QARA consultant performance and long-term in-house RA/QA hiring strategy.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'regulatory-expertise-acquisition',
                                        'Regulatory Expertise — Role Fulfilment',
                                        'RA/QA roles filled vs required for submissions: QARA consultant active, in-house RA/QA role pipeline',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'internal-regulatory-hiring',
                                        'In-house RA/QA Build',
                                        'Progress on hiring a permanent internal RA/QA function (post-clearance); current consultant vs FTE plan',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'qara-consultant-performance',
                                        'QARA Consultant Performance',
                                        'Samantha Baxter deliverable tracker: SOPs on schedule, audit prep complete, open actions',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'regulatory-training-currency',
                                        'Regulatory Training Currency',
                                        '# exec team members with current regulatory training (MDR, FDA QMSR, IEC 62304); training log',
                                        'CSO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.3 — IP Protection & Patent Portfolio ────────
                            createMetric(
                                'l2-ip-patent-portfolio',
                                'IP Protection & Patent Portfolio',
                                'Protect Cardiovolt\'s AI-ECG algorithms and clinical methods via patent portfolio (Imperial College collaboration). Maintain freedom-to-operate in US and EU markets against Anumana, Eko and others.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'patent-application-filing',
                                        'Patent Filing — Imperial College',
                                        'Patent application(s) filed via Imperial College: filing status, territory coverage (US/EP), claims scope',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'freedom-to-operate-analysis',
                                        'Freedom to Operate Analysis',
                                        'FTO landscape review complete for all 9 AI-ECG models; risk rating per model; competitor monitoring',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'patent-prosecution-management',
                                        'Patent Prosecution',
                                        '# patent applications in prosecution; office actions responded to on time; grant status',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'ip-commercialisation-strategy',
                                        'IP Commercialisation Strategy',
                                        'Licensing strategy defined for Imperial IP; exclusivity terms confirmed; defensive IP mapping complete',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Scientific Publications & Evidence ───────
                            createMetric(
                                'l2-scientific-publications',
                                'Scientific Publications & Evidence',
                                'Build the peer-reviewed publication portfolio that underpins clinical credibility, regulatory submissions, and commercial differentiation. Target journals: Nature Medicine, Lancet Digital Health, JAMA Cardiology, European Heart Journal.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'manuscript-development',
                                        'Manuscripts in Development',
                                        '# manuscripts in active writing / revision; target journal confirmed; submission timeline',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'peer-review-timeline',
                                        'Peer Review Progress',
                                        '# manuscripts under review; revision cycles completed; acceptance rate by journal tier',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'citation-impact-tracking',
                                        'Citation Impact',
                                        '# citations on published Cardiovolt / team papers; h-index trajectory; Altmetric scores',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'publication-pipeline-target',
                                        'Publication Pipeline Target',
                                        '# publications planned for 2026; proportion directly supporting regulatory submissions or CER',
                                        'CSO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.5 — Team Retention & Development ────────────
                            createMetric(
                                'l2-team-retention-development',
                                'Team Retention & Development',
                                'Retain top-quartile performers and maintain healthy team development cadence. For a 5–8 person pre-clearance SaMD team, losing any senior technical or regulatory person is a critical risk.',
                                'CEO',
                                true,
                                [
                                    createMetric(
                                        'time-to-hire',
                                        'Time to Hire (General)',
                                        'Average days from role posting to accepted offer across all open roles; benchmark vs peer startups',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'retention-rates-top-quartile',
                                        'Top-quartile Retention Rate',
                                        '% of top performers retained over rolling 12 months; exit interview themes tracked',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'attrition-rates',
                                        'Attrition Rate',
                                        'Total annualised attrition %; good attrition (bottom quartile) vs regrettable attrition tracked separately',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'training-days',
                                        'Training Days Per Person',
                                        'Total training days per employee per quarter; regulatory CPD, technical certifications and leadership tracked separately',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'succession-planning',
                                        'Critical Role Succession Coverage',
                                        '% of critical roles (CEO, CSO, HoSWE, QARA) with an identified succession plan or documented cover arrangement',
                                        'CEO',
                                        true
                                    ),
                                ]
                            ),
                        ]
                    ),

                    // ═══════════════════════════════════════════════════════════
                    //  L1.2 — STRATEGIC NETWORK, SUCCESSION & CLINICAL READINESS
                    //  Active | KOL/advisory network, clinical integration,
                    //  org resilience
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-network-readiness',
                        'L1.2  Strategic Network, Succession & Clinical Readiness',
                        'Build the external network (KOL, advisory, regulatory, clinical) and internal capability (product deployment, org resilience) that converts clearance into commercial momentum.',
                        'CMO',
                        true,
                        [
                            // ─── L2.1 — KOL & Advisory Network ──────────────────
                            createMetric(
                                'l2-kol-advisory',
                                'KOL & Advisory Network',
                                'Build relationships with key opinion leaders in cardiology and clinical AI. KOL endorsements, advisory board cadence and pharma introductions directly support CRO pipeline and post-clearance health system adoption.',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'kol-identification-outreach',
                                        'KOL Identification & Outreach',
                                        '# new KOL relationships initiated per month; tier classification (Tier 1: major academic centre PI)',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'advisory-board-engagement',
                                        'Advisory Board Engagement',
                                        '# advisory board meetings held per quarter; advisor deliverables (intro letters, study design, endorsements)',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'kol-referral-generation',
                                        'KOL Referrals & Warm Introductions',
                                        '# warm introductions to health systems, CROs or payers generated via KOL network per quarter',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'pharma-sponsor-introductions',
                                        'Pharma / Sponsor Introductions',
                                        '# new pharma sponsor introductions via KOL or advisory network; stage in CRO pipeline',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'cro-relationship-depth',
                                        'CRO Relationship Depth',
                                        'Quality score for top 5 active CRO relationships: sponsor satisfaction, renewal intent, expansion potential',
                                        'CMO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.2 — Clinical & Regulatory Network ───────────
                            createMetric(
                                'l2-clinical-regulatory-network',
                                'Clinical & Regulatory Network',
                                'Maintain the NHS clinical network underpinning the pilot and future expansion, plus the regulatory agency and expert network supporting submissions (FDA pre-sub, NB engagement, advocacy letters).',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'regulatory-support-network',
                                        'Regulatory Agency Relations',
                                        'Active relationships with FDA reviewers (via pre-sub), Scarlet NB contacts, and MHRA/BSI liaisons',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'regulatory-letter-support',
                                        'Regulatory Letter of Support',
                                        '# clinical KOLs and institutions providing letters of support for FDA / MDR submission; quality of signatories',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'nhs-clinical-network-depth',
                                        'NHS Clinical Network Depth',
                                        '# NHS clinicians actively engaged beyond pilot site; breadth of trust coverage for future expansion',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'academic-collaborations',
                                        'Academic Collaborations',
                                        '# active academic collaborations (Imperial College, NHS trusts, US academic medical centres) supporting publications or validation studies',
                                        'CSO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.3 — Product & Clinical Deployment Readiness ──
                            createMetric(
                                'l2-product-deployment-readiness',
                                'Product & Clinical Deployment Readiness',
                                'Track readiness of the AI-ECG platform for scaled clinical deployment: EHR integration quality, UI usability, report generation and workflow efficiency. HoSWE owns from June 2026.',
                                'HoSWE',
                                true,
                                [
                                    createMetric(
                                        'clinical-workflow-integration',
                                        'Clinical Workflow Integration',
                                        'Workflow compatibility score: AI-ECG output integrated into clinical decision pathway without added friction',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'ehr-integration-testing',
                                        'EHR Integration Testing',
                                        'FHIR R4 integration test pass rate across target EHR systems; regression test cadence',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'clinical-user-interface',
                                        'Clinical User Interface Quality',
                                        'Clinician usability score (IEC 62366-1 summative study outcomes); SUS score ≥ 70',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'report-generation-quality',
                                        'Report Generation Quality',
                                        'AI-ECG report accuracy and formatting score; clinician-reported clarity and actionability rating',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'workflow-efficiency-testing',
                                        'Workflow Efficiency Testing',
                                        'Measured reduction in time per ECG review vs pre-AI baseline; clinical pathway impact documented',
                                        'HoSWE',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Succession Planning & Org Design ────────
                            createMetric(
                                'l2-succession-org-design',
                                'Succession Planning & Org Design',
                                'Ensure organisational resilience: every critical function has a documented cover plan. Design the org structure to scale from ~6 FTE (2026) to ~15 FTE (post-clearance commercial team).',
                                'CEO',
                                true,
                                [
                                    createMetric(
                                        'critical-role-cover-plans',
                                        'Critical Role Cover Plans',
                                        '# critical roles (CEO, CSO, HoSWE, QARA lead) with documented short-term cover arrangements',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'org-structure-review',
                                        'Org Structure Review',
                                        'Quarterly org chart review vs headcount plan; gaps vs hiring pipeline; reporting line clarity',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'exec-team-skill-balance',
                                        'Executive Team Skill Balance',
                                        'Coverage assessment across clinical, engineering, regulatory, commercial and financial domains at exec level',
                                        'CEO',
                                        true
                                    ),
                                    createMetric(
                                        'post-clearance-hiring-plan',
                                        'Post-clearance Hiring Plan',
                                        'Series A headcount plan ready: roles, costs, timing for commercial launch team (US BD, clinical specialist, support)',
                                        'CEO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.5 — Talent Pipeline & Culture ───────────────
                            createMetric(
                                'l2-talent-pipeline-culture',
                                'Talent Pipeline & Culture',
                                'Build a talent pipeline and culture that attracts and retains exceptional people. Intern programme via Imperial College and UK academic network. Culture metrics active once team exceeds ~8 FTE.',
                                'CEO',
                                false,
                                [
                                    createMetric(
                                        'intern-programme',
                                        'Imperial College Intern Programme',
                                        '# interns placed via Imperial College MSc/PhD pipeline per year; conversion to FTE offers',
                                        'CEO',
                                        false
                                    ),
                                    createMetric(
                                        'grad-recruitment-pipeline',
                                        'Graduate Recruitment Pipeline',
                                        '# graduate candidates in pipeline for junior technical and clinical roles; source channel breakdown',
                                        'CEO',
                                        false
                                    ),
                                    createMetric(
                                        'employee-nps',
                                        'Employee NPS',
                                        'eNPS score from quarterly pulse survey; trend tracked; action themes from detractors',
                                        'CEO',
                                        false
                                    ),
                                    createMetric(
                                        'culture-and-values-alignment',
                                        'Culture & Values Alignment',
                                        'Annual culture assessment: team alignment on mission, values and ways-of-working; action plan from gaps',
                                        'CEO',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                ]
            }
        ]
    },
    {
        id: 'change',
        name: 'CHANGE',
        description: 'Product, Models & Competitive Position',
        goal: 'Deliver validated, regulatory-grade AI-ECG models for 9 indications by Dec 2026. Maintain a durable competitive moat through clinical evidence superiority, publication output, and regulatory science leadership vs Anumana, Eko Health, and AliveCor.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Velocity',
                description: 'CHANGE — AI model development & validation → competitive position & innovation horizon',
                metrics: [

                    // ═══════════════════════════════════════════════════════════
                    //  L1.1 — AI MODEL DEVELOPMENT & VALIDATION
                    //  Active | 9-model AI-ECG programme: training → validation →
                    //  regulatory evidence → submission-ready packages
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-ai-model-development',
                        'L1.1  AI Model Development & Validation',
                        'Develop, validate and generate regulatory-grade evidence packages for all 9 AI-ECG models (AFib, LVH, cardiomyopathy, LQTS, HCM, etc.) to support FDA 510(k) and EU MDR submissions by Dec 2026. HoSWE owns model infrastructure and MLOps from June 2026; CSO owns clinical validation strategy.',
                        'HoSWE',
                        true,
                        [
                            // ─── L2.1 — Model Performance & Training ────────────
                            createMetric(
                                'l2-model-performance-training',
                                'Model Performance & Training',
                                'Track AI-ECG model training velocity and performance optimisation across all 9 models. AUC improvement, dataset coverage and interpretability are primary indicators of submission-readiness. HoSWE leads from June 2026.',
                                'HoSWE',
                                true,
                                [
                                    createMetric(
                                        'model-performance-optimization',
                                        'Model Performance Optimisation',
                                        'Accuracy, calibration and sensitivity/specificity per model; delta vs pre-defined performance thresholds for submission',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'model-training-cycles',
                                        'Training Cycles',
                                        '# training experiments per week per model; convergence rate; compute efficiency per training run',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'auc-improvement-tracking',
                                        'AUC Progression per Model',
                                        'AUC trend per AI-ECG model (target thresholds defined per indication); rate of improvement vs timeline',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'validation-dataset-coverage',
                                        'Validation Dataset Coverage',
                                        '% demographic and clinical subgroup coverage in validation dataset; FDA-required diversity metrics met',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'model-interpretability-testing',
                                        'Model Interpretability',
                                        'Explainability features implemented (SHAP / saliency maps); clinician comprehension score from usability testing',
                                        'HoSWE',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.2 — Regulatory Validation Evidence ──────────
                            createMetric(
                                'l2-regulatory-validation-evidence',
                                'Regulatory Validation Evidence',
                                'Generate the clinical and real-world evidence required for 510(k) Safety and Performance Summary and MDR CER. Independent studies, NHS real-world data and bias assessment all feed directly into submissions.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'clinical-validation-studies',
                                        'Independent Validation Studies',
                                        '# independent clinical validation studies active; patient enrolment progress; primary endpoint status per model',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'real-world-performance',
                                        'Real-world Performance',
                                        'Model performance in NHS pilot and CRO settings vs development dataset; distribution shift metrics',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'bias-fairness-assessment',
                                        'Bias & Fairness Assessment',
                                        'Algorithmic fairness across age, sex, ethnicity and comorbidity subgroups; IMDRF AI/ML bias framework compliance',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'longitudinal-model-performance-tracking',
                                        'Longitudinal Performance Stability',
                                        'Model performance stability over time post-deployment; concept drift detection; PMCF evidence generation',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'validation-datasets',
                                        'Validation Datasets',
                                        'External datasets used for independent model validation; tracks access status, cohort size and regulatory acceptance for 510(k) Safety & Performance Summary and MDR CER submissions.',
                                        'HoSWE',
                                        true,
                                        [
                                            createMetric(
                                                'dataset-bidmc',
                                                'BIDMC Test Dataset',
                                                'Beth Israel Deaconess Medical Center ECG dataset — primary external test set for 510(k) evidence; access confirmed',
                                                'HoSWE',
                                                true
                                            ),
                                            createMetric(
                                                'dataset-vumc',
                                                'VUMC Dataset',
                                                'Vanderbilt University Medical Center ECG dataset; data access agreement pending',
                                                'HoSWE',
                                                false
                                            ),
                                            createMetric(
                                                'dataset-fritz',
                                                'FRITZ Dataset',
                                                'FRITZ ECG dataset; access and validation protocol pending',
                                                'HoSWE',
                                                false
                                            ),
                                            createMetric(
                                                'dataset-eu',
                                                'EU Validation Dataset',
                                                'European ECG cohort for MDR CER geographic generalisability evidence; data access agreement pending',
                                                'HoSWE',
                                                false
                                            ),
                                        ]
                                    ),
                                ]
                            ),

                            // ─── L2.3 — Model Infrastructure & MLOps ────────────
                            createMetric(
                                'l2-model-infrastructure-mlops',
                                'Model Infrastructure & MLOps',
                                'Build the software infrastructure for reliable, auditable and IEC 62304-compliant model development, deployment and monitoring. HoSWE owns from June 2026. Pre-clearance requirements: version control, change management, CI/CD pipeline.',
                                'HoSWE',
                                true,
                                [
                                    createMetric(
                                        'model-deployment-pipeline',
                                        'Model Deployment Pipeline',
                                        'CI/CD pipeline for model releases: automated testing, versioned deployment, rollback capability per IEC 62304 §7',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'training-data-governance',
                                        'Training Data Governance',
                                        'Data quality, lineage and versioning for all training datasets; consent tracking; data ingestion audit log',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'model-version-registry',
                                        'Model Version Registry',
                                        'Centralised registry of all deployed model versions with change history, performance records and regulatory status',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'automated-model-retraining-pipeline',
                                        'Automated Retraining Pipeline',
                                        'Automated retraining triggered by drift detection or new data; PCCP-aligned change control gate before re-deployment',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'model-drift-detection-alerting',
                                        'Model Drift Detection & Alerting',
                                        'Automated performance degradation detection in production; alert thresholds defined; incident response SLA',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Multi-model Bundle & Indication Scope ────
                            createMetric(
                                'l2-multimodel-scope',
                                'Multi-model Bundle & Indication Scope',
                                'Define and track which AI-ECG models enter the initial 510(k)/MDR submission vs post-clearance expansion. Manage the indication roadmap for models beyond the initial 9-model bundle.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'nine-model-submission-readiness',
                                        '9-model Submission Readiness',
                                        '% of 9 AI-ECG models with complete evidence packages (AUC + clinical study + bias assessment) for submission',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'indication-expansion-roadmap',
                                        'Indication Expansion Roadmap',
                                        'Prioritised pipeline of AI-ECG models beyond initial 9: LBBB, RV disease, Brugada, etc.; development timeline',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'multimodal-ecg-pipeline',
                                        'Multimodal ECG Pipeline',
                                        'Development pipeline for models combining ECG with imaging (echo, CMR) or wearable data; feasibility study status',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'federated-learning-performance-optimization',
                                        'Federated Learning',
                                        'Performance and efficiency of federated training across NHS and CRO data nodes; privacy-preserving model improvement',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.5 — SaMD Algorithm Governance ───────────────
                            createMetric(
                                'l2-samd-algorithm-governance',
                                'SaMD Algorithm Governance',
                                'Ensure all AI-ECG model changes are governed by a rigorous, auditable process aligned with IEC 62304 change management, FDA PCCP framework, and EU AI Act requirements. Protects regulatory clearances post-launch.',
                                'HoSWE',
                                true,
                                [
                                    createMetric(
                                        'algorithm-change-impact-assessment',
                                        'Algorithm Change Impact Assessment',
                                        '% of model updates with completed impact assessment before deployment; open CAB (Change Advisory Board) items',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'pccp-alignment',
                                        'PCCP Framework Alignment',
                                        'Predetermined Change Control Plan drafted; % of planned model updates covered by PCCP scope',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'algorithm-transparency-documentation',
                                        'Algorithm Transparency',
                                        'Model cards published per model: training data, performance, limitations, intended use — ready for clinician disclosure',
                                        'HoSWE',
                                        true
                                    ),
                                    createMetric(
                                        'post-market-algorithm-improvement',
                                        'Post-market Algorithm Improvement',
                                        'Structured feedback loop from PMS / PMCF data into model improvement cycle; improvement velocity post-clearance',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                    // ═══════════════════════════════════════════════════════════
                    //  L1.2 — COMPETITIVE POSITION & INNOVATION HORIZON
                    //  Active (intelligence) | Inactive (future horizon)
                    //  Monitor the AI-ECG landscape; sharpen differentiation;
                    //  build regulatory science leadership
                    // ═══════════════════════════════════════════════════════════
                    createMetric(
                        'l1-competitive-position',
                        'L1.2  Competitive Position & Innovation Horizon',
                        'Monitor the AI-ECG SaMD competitive landscape (Anumana, Eko Health, AliveCor, Heartflow), communicate Cardiovolt\'s clinical and regulatory differentiation, and define the future technology horizon. CSO leads intelligence; CMO leads positioning.',
                        'CSO',
                        true,
                        [
                            // ─── L2.1 — Competitive Intelligence ────────────────
                            createMetric(
                                'l2-competitive-intelligence',
                                'Competitive Intelligence',
                                'Systematic monitoring of competitor regulatory filings, clinical evidence, partnerships and IP activity. Primary competitors: Anumana (LivaNova), Eko Health, AliveCor, Heartflow, Caption Health. CSO leads weekly scan.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'competitive-landscape-monitoring',
                                        'Competitive Landscape Monitoring',
                                        'Monthly competitor analysis update: product features, clinical claims, pricing signals, funding status',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'competitor-regulatory-filings',
                                        'Competitor Regulatory Filings',
                                        'FDA 510(k) / De Novo and EMA MDR filings by named AI-ECG competitors; clearance timeline implications',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'competitive-performance-benchmarking',
                                        'Performance Benchmarking',
                                        'Head-to-head AUC / sensitivity / specificity comparison vs published Anumana and Eko Health data',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'patent-landscape-monitoring',
                                        'Patent Landscape Monitoring',
                                        'New patent filings by AI-ECG competitors; FTO impact assessment; defensive publication triggers',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'competitive-partnership-intelligence',
                                        'Competitor Partnership Intelligence',
                                        'CRO, pharma and EHR partnership deals by competitors; implications for Cardiovolt\'s pipeline strategy',
                                        'CMO',
                                        true
                                    ),
                                ]
                            ),

                            // ─── L2.2 — Market Differentiation & Thought Leadership
                            createMetric(
                                'l2-differentiation-thought-leadership',
                                'Market Differentiation & Thought Leadership',
                                'Communicate and reinforce Cardiovolt\'s unique advantages: multi-disease AI-ECG bundle, peer-reviewed evidence, dual regulatory pathway leadership, and NHS real-world validation. Build the brand that wins in clinical sales cycles.',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'clinical-evidence-superiority',
                                        'Clinical Evidence Superiority',
                                        'Cardiovolt evidence package vs comparable competitor publications: sample size, AUC, independent validation status',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'technology-advantage-communication',
                                        'Technology Differentiation Messaging',
                                        'Key differentiators clearly articulated in sales materials, pitch deck, and clinical presentations; win interview score',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'market-positioning-effectiveness',
                                        'Market Positioning Effectiveness',
                                        'Cardiovolt brand recognition score among target cardiologist and CRO audiences; share of voice vs peers',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'thought-leadership-development',
                                        'Thought Leadership Development',
                                        '# conference presentations, invited talks, media mentions and podcast appearances per quarter',
                                        'CSO',
                                        true
                                    ),
                                    createMetric(
                                        'regulatory-pathway-leadership',
                                        'Regulatory Science Leadership',
                                        'Cardiovolt cited or consulted on AI/ML SaMD regulatory frameworks (IMDRF, FDA dockets, MDR guidance)',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.3 — Win Rate & Commercial Intelligence ───────
                            createMetric(
                                'l2-win-rate-commercial-intel',
                                'Win Rate & Commercial Intelligence',
                                'Track outcomes of competitive evaluations and build a structured win/loss intelligence programme. Use findings to sharpen product, messaging, and evidence strategy.',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'head-to-head-pilot-win-rate',
                                        'Head-to-head Pilot Win Rate',
                                        '% of competitive evaluations (CRO or health system) where Cardiovolt is selected over an alternative; by competitor',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'win-loss-analysis-completion',
                                        'Win/Loss Analysis',
                                        '% of won and lost deals with completed debrief; top 3 win reasons and loss reasons tracked quarterly',
                                        'CMO',
                                        true
                                    ),
                                    createMetric(
                                        'competitive-win-rate-analysis',
                                        'Win Rate Trend Analysis',
                                        'Win rate trend by quarter; loss reason categories mapped to product/evidence/pricing/regulatory gaps',
                                        'CMO',
                                        false
                                    ),
                                    createMetric(
                                        'social-media-competitive-sentiment',
                                        'Competitive Brand Sentiment',
                                        'Cardiovolt vs competitor share-of-voice on LinkedIn, X/Twitter, CardioNerds and clinical AI forums',
                                        'CMO',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.4 — Future Technology Horizon ───────────────
                            createMetric(
                                'l2-future-technology-horizon',
                                'Future Technology Horizon',
                                'Define and explore the next generation of Cardiovolt technology beyond the initial 510(k)/MDR scope: continuous learning, multimodal AI, federated networks and wearable ECG integration.',
                                'HoSWE',
                                false,
                                [
                                    createMetric(
                                        'continuous-learning-framework',
                                        'Continuous Learning Framework',
                                        'Architecture design for PCCP-compliant continuous model improvement post-clearance; regulatory strategy confirmed',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'wearable-ecg-integration',
                                        'Wearable ECG Integration',
                                        'Feasibility of AI-ECG models on wearable / patch ECG inputs (AliveCor KardiaMobile, Zio patch); accuracy delta vs 12-lead',
                                        'HoSWE',
                                        false
                                    ),
                                    createMetric(
                                        'multimodal-fusion-research',
                                        'Multimodal AI Research',
                                        'Research pipeline for ECG + echocardiography or CMR fusion models; feasibility publication in progress',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'real-world-learning-network',
                                        'Real-world Learning Network',
                                        'Design of multi-site federated learning network (NHS trusts + CRO data) for post-clearance model improvement',
                                        'HoSWE',
                                        false
                                    ),
                                ]
                            ),

                            // ─── L2.5 — Regulatory Science & AI Ethics Leadership
                            createMetric(
                                'l2-regulatory-science-leadership',
                                'Regulatory Science & AI Ethics Leadership',
                                'Position Cardiovolt as a credible voice shaping AI/ML SaMD regulation and clinical AI ethics. Contributions to FDA, MHRA, IMDRF guidance processes and peer-reviewed regulatory science. Inactive until initial clearance creates the credibility platform.',
                                'CSO',
                                false,
                                [
                                    createMetric(
                                        'fda-imdrf-contribution',
                                        'FDA / IMDRF Regulatory Contribution',
                                        '# submissions to FDA dockets, IMDRF working groups or MHRA guidance consultations on AI/ML SaMD',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'ai-ethics-framework-adoption',
                                        'AI Ethics Framework',
                                        'Cardiovolt AI ethics principles published; alignment with WHO AI ethics for health and NHS AI Lab principles documented',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'regulatory-science-conference-presence',
                                        'Regulatory Science Conference Presence',
                                        '# presentations at regulatory science forums (RAPS, DIA, EMA workshops) on AI/ML ECG SaMD pathways',
                                        'CSO',
                                        false
                                    ),
                                    createMetric(
                                        'industry-standard-participation',
                                        'Industry Standards Participation',
                                        'Participation in IEEE, IEC or ISO working groups defining next-generation AI/ML SaMD standards',
                                        'CSO',
                                        false
                                    ),
                                ]
                            ),
                        ]
                    ),

                ]
            }
        ]
    }
];

export const scorecardData4: ScorecardData = {
    title: "CARDIOVOLT.AI — BALANCED SCORECARD",
    subtitle: "Pre-Revenue SaMD | May 2026",
    currentVersion: scorecardData4Categories,
    versions: [],
    lastModified: new Date()
};